import { EditOutlined, UserOutlined } from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { useDisclosure } from "hooks/useDisclosure"
import { BalloonIcon } from "icons/BalloonIcon"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { useFlag } from "modules/flags/flags"
import { Fragment, ReactNode } from "react"
import { useParams } from "react-router"
import { useUnreadMessagesCount } from "services/communications"
import { usePatientDetails } from "services/patients"
import { useOrganization, useUserRole } from "services/users"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { formatDate } from "utils/date"
import { DeviceStatus } from "./DeviceStatus"
import { EditProfileNote } from "./EditProfileNote"
import { PatientDiagnosisCodes } from "./PatientDiagnosisCodes"

export const PatientProfile = () => {
	const { userId: patientId } = useParams()
	const { data: patient } = usePatientDetails(patientId)
	const { data: { isProvider, isProviderAnon } = {} } = useUserRole()
	const { data: unreadMessagesCount } = useUnreadMessagesCount(
		isProvider ? patientId : undefined,
	)
	const organization = useOrganization()
	const editingNote = useDisclosure()
	const { clinicalNotes } = useFlag("clinicalNotes")

	if (!patient) {
		return null
	}

	return (
		<div
			role="region"
			aria-label="Patient profile"
			className="@container flex gap-6 rounded-[20px] border border-mint-20 p-6 shadow-secondary"
		>
			<Avatar
				className="min-h-12 min-w-12 @3xl:min-h-20 @3xl:min-w-20"
				icon={isProviderAnon ? <UserOutlined aria-hidden /> : undefined}
			>
				{!isProviderAnon ? patient.fullName : DEFAULT_PLACEHOLDER}
			</Avatar>
			<div className="flex w-full flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="flex flex-col items-start justify-between gap-3 @3xl:flex-row">
						<h2 className="font-headings text-3xl font-semibold wrap-break-word">
							{!isProviderAnon ? patient.fullName : DEFAULT_PLACEHOLDER}
						</h2>

						{!isProviderAnon && (
							<div className="flex gap-3">
								{!editingNote.isOpen && clinicalNotes.enabled && (
									<Button
										icon={<EditOutlined aria-hidden />}
										onClick={editingNote.open}
									>
										Edit
									</Button>
								)}

								<Voice.CallToAction
									show={isProvider && organization?.hasSend}
									patient={patient}
								/>
								<Chat.CallToAction
									view={!!organization?.hasView}
									send={!!organization?.hasSend}
									patient={patient}
									unreadMessagesCount={unreadMessagesCount}
								/>
							</div>
						)}
					</div>

					<div className="flex flex-wrap items-center gap-1 text-navy-80">
						{(
							[
								[
									"address",
									!isProviderAnon && !!patient.address,
									patient.address,
								],
								[
									"dateOfBirth",
									!isProviderAnon && patient.dateOfBirth,
									<span className="flex items-center gap-1">
										<BalloonIcon aria-hidden className="size-4" />
										{formatDate(patient.dateOfBirth!)}
										{patient.age ? ` (${patient.age} years)` : null}
									</span>,
								],
								[
									"codes",
									!isProviderAnon && clinicalNotes.enabled,
									<PatientDiagnosisCodes patientId={patient.id} />,
								],
								["deviceStatus", true, <DeviceStatus />],
							] as [string, boolean, ReactNode][]
						).map(([key, isVisible, element], index) => {
							if (!isVisible) {
								return null
							}

							return (
								<Fragment key={key}>
									{index > 0 && <Divider vertical />}
									{element}
								</Fragment>
							)
						})}
					</div>
				</div>

				{patientId && clinicalNotes.enabled && (
					<EditProfileNote
						patientId={patientId}
						isEditing={editingNote.isOpen}
						onClose={editingNote.close}
					/>
				)}
			</div>
		</div>
	)
}
